import React, { useState, useEffect, useCallback } from 'react';
import {
    Save,
    Loader2,
    Euro,
    CheckCircle2,
    XCircle,
    Info,
    AlertCircle,
    ChevronDown,
    Settings,
    Users,
    User,
    Calendar,
    Copy,
    Sparkles,
    Clock,
    Trophy
} from 'lucide-react';
import Toast from '../Common/Toast';
import './FeeManager.css';
import API_BASE_URL from '../../config/api';

const AVAILABLE_SEASONS = [
    '2023/2024',
    '2024/2025',
    '2025/2026',
    '2026/2027',
    '2027/2028',
    '2028/2029'
];

const FeeInput = ({ label, field, sport, onChange }) => (
    <div className="fee-input-container">
        <label>{label}</label>
        <div className="input-group">
            <span className="currency-symbol">€</span>
            <input
                type="number"
                step="0.50"
                min="0"
                value={sport[field]}
                onChange={e => onChange(sport.id, field, e.target.value)}
                onClick={(e) => e.stopPropagation()}
            />
        </div>
    </div>
);

const FeeManager = () => {
    // Determine active sports season (September 1st to August 31st)
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1-12
    const currentSeasonStartYear = currentMonth >= 9 ? currentYear : currentYear - 1;
    const currentActiveSeason = `${currentSeasonStartYear}/${currentSeasonStartYear + 1}`;

    const [selectedSeason, setSelectedSeason] = useState(currentActiveSeason);
    const [memberFee, setMemberFee] = useState(0);
    const [minorMemberFee, setMinorMemberFee] = useState(0);
    const [admissionFee, setAdmissionFee] = useState(5);
    const [sports, setSports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [copyingPrevious, setCopyingPrevious] = useState(false);
    const [savingGlobal, setSavingGlobal] = useState(false);
    const [savingSport, setSavingSport] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [expandedSports, setExpandedSports] = useState([]);
    const [isExplicitGlobal, setIsExplicitGlobal] = useState(false);

    const addToast = useCallback((message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const fetchFees = useCallback(async (season = selectedSeason) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/fees?season=${encodeURIComponent(season)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Falha ao carregar quotas e mensalidades da época.');
            const data = await res.json();
            setMemberFee(data.memberFee);
            setMinorMemberFee(data.minorMemberFee || 0);
            setAdmissionFee(data.admissionFee !== undefined ? data.admissionFee : 5);
            setIsExplicitGlobal(!!data.isExplicitGlobal);
            setSports((data.sports || []).map(s => ({
                ...s,
                feeNormalNormal: s.feeNormalNormal ?? 0,
                feeEscalao1Normal: s.feeEscalao1Normal ?? 0,
                feeEscalao2Normal: s.feeEscalao2Normal ?? 0,
                feeDiscount: s.feeDiscount ?? 0,
                inscriptionFeeNormal: s.inscriptionFeeNormal ?? 0,
                inscriptionFeeMinis: s.inscriptionFeeMinis ?? 0,
                inscriptionFeeMinisDiscount: s.inscriptionFeeMinisDiscount ?? 0,
                inscriptionFeeVeteranos: s.inscriptionFeeVeteranos ?? 0,
                feeVeteranos: s.feeVeteranos ?? 0,
                quotaIncluded: s.quotaIncluded ?? true,
                annualTicketPriceSocio: s.annualTicketPriceSocio ?? 40,
                annualTicketPriceNonSocio: s.annualTicketPriceNonSocio ?? 40,
                isExplicitForSeason: s.isExplicitForSeason ?? false
            })));
        } catch (err) {
            addToast(err.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [selectedSeason, addToast]);

    useEffect(() => {
        fetchFees(selectedSeason);
    }, [selectedSeason, fetchFees]);

    const handleCopyPreviousSeason = async () => {
        const startYear = parseInt(selectedSeason.split('/')[0], 10);
        const prevSeason = `${startYear - 1}/${startYear}`;
        setCopyingPrevious(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/fees?season=${encodeURIComponent(prevSeason)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error(`Falha ao obter valores da Época ${prevSeason}.`);
            const data = await res.json();
            setMemberFee(data.memberFee);
            setMinorMemberFee(data.minorMemberFee || 0);
            setAdmissionFee(data.admissionFee !== undefined ? data.admissionFee : 5);
            setSports((data.sports || []).map(s => ({
                ...s,
                feeNormalNormal: s.feeNormalNormal ?? 0,
                feeEscalao1Normal: s.feeEscalao1Normal ?? 0,
                feeEscalao2Normal: s.feeEscalao2Normal ?? 0,
                feeDiscount: s.feeDiscount ?? 0,
                inscriptionFeeNormal: s.inscriptionFeeNormal ?? 0,
                inscriptionFeeMinis: s.inscriptionFeeMinis ?? 0,
                inscriptionFeeMinisDiscount: s.inscriptionFeeMinisDiscount ?? 0,
                inscriptionFeeVeteranos: s.inscriptionFeeVeteranos ?? 0,
                feeVeteranos: s.feeVeteranos ?? 0,
                quotaIncluded: s.quotaIncluded ?? true,
                annualTicketPriceSocio: s.annualTicketPriceSocio ?? 40,
                annualTicketPriceNonSocio: s.annualTicketPriceNonSocio ?? 40,
                isExplicitForSeason: false
            })));
            addToast(`Valores copiados da Época ${prevSeason}. Pode agora ajustar e gravar para a Época ${selectedSeason}.`, 'info');
        } catch (err) {
            addToast(err.message, 'error');
        } finally {
            setCopyingPrevious(false);
        }
    };

    const handleSportField = (id, field, value) => {
        setSports(prev => prev.map(s =>
            s.id === id
                ? { ...s, [field]: value }
                : s
        ));
    };

    const toggleSportExpand = (id) => {
        setExpandedSports(prev =>
            prev.includes(id) ? prev.filter(sId => sId !== id) : [...prev, id]
        );
    };

    const saveGlobalFee = async () => {
        setSavingGlobal(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/fees/global?season=${encodeURIComponent(selectedSeason)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    amount: parseFloat(memberFee),
                    minorAmount: parseFloat(minorMemberFee),
                    admissionFee: parseFloat(admissionFee),
                    season: selectedSeason
                })
            });
            if (!res.ok) throw new Error('Falha ao guardar quotas globais.');
            setIsExplicitGlobal(true);
            addToast(`Quotas e jóia de admissão gravadas para a Época ${selectedSeason}!`);
        } catch (err) {
            addToast(err.message, 'error');
        } finally {
            setSavingGlobal(false);
        }
    };

    const saveSportFee = async (e, sport) => {
        e.stopPropagation();
        setSavingSport(sport.id);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/fees/sport/${sport.id}?season=${encodeURIComponent(selectedSeason)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    feeNormalNormal: parseFloat(sport.feeNormalNormal) || 0,
                    feeEscalao1Normal: parseFloat(sport.feeEscalao1Normal) || 0,
                    feeEscalao2Normal: parseFloat(sport.feeEscalao2Normal) || 0,
                    feeDiscount: parseFloat(sport.feeDiscount) || 0,
                    inscriptionFeeNormal: parseFloat(sport.inscriptionFeeNormal) || 0,
                    inscriptionFeeMinis: parseFloat(sport.inscriptionFeeMinis) || 0,
                    inscriptionFeeMinisDiscount: parseFloat(sport.inscriptionFeeMinisDiscount) || 0,
                    inscriptionFeeVeteranos: parseFloat(sport.inscriptionFeeVeteranos) || 0,
                    feeVeteranos: parseFloat(sport.feeVeteranos) || 0,
                    quotaIncluded: sport.quotaIncluded,
                    annualTicketPriceSocio: parseFloat(sport.annualTicketPriceSocio) || 0,
                    annualTicketPriceNonSocio: parseFloat(sport.annualTicketPriceNonSocio) || 0,
                    season: selectedSeason
                })
            });
            if (!res.ok) throw new Error(`Falha ao guardar ${sport.name}.`);
            
            // Mark sport as explicitly configured for this season
            setSports(prev => prev.map(s => s.id === sport.id ? { ...s, isExplicitForSeason: true } : s));
            addToast(`${sport.name} gravado com sucesso para a Época ${selectedSeason}!`);
        } catch (err) {
            addToast(err.message, 'error');
        } finally {
            setSavingSport(null);
        }
    };

    const isCurrentSeason = selectedSeason === currentActiveSeason;
    const startYearNumber = parseInt(selectedSeason.split('/')[0], 10);

    return (
        <div className="fee-manager animate-in">
            {/* Toasts Container */}
            <div className="toast-container">
                {toasts.map(t => (
                    <Toast key={t.id} {...t} onClose={() => removeToast(t.id)} />
                ))}
            </div>

            <header className="fee-header">
                <div className="header-content">
                    <h1>Gestão de Quotas e Preços</h1>
                    <p className="header-subtitle">Tabela de mensalidades, taxas de inscrição e quotas por Época Desportiva.</p>
                </div>

                {/* ── Season Selector Controls ── */}
                <div className="fee-period-selector-bar">
                    <div className="fee-period-group">
                        <Trophy size={18} className="period-icon" />
                        <div className="fee-select-wrapper">
                            <select
                                value={selectedSeason}
                                onChange={(e) => setSelectedSeason(e.target.value)}
                                aria-label="Selecionar Época Desportiva"
                            >
                                {AVAILABLE_SEASONS.map((season) => (
                                    <option key={season} value={season}>Época {season}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <button
                        className="fee-copy-btn"
                        onClick={handleCopyPreviousSeason}
                        disabled={copyingPrevious || loading}
                        title="Copiar valores da época desportiva anterior"
                    >
                        {copyingPrevious ? <Loader2 size={16} className="spinner" /> : <Copy size={16} />}
                        <span>Copiar Época Anterior</span>
                    </button>
                </div>
            </header>

            {/* ── Active Season Info Banner ── */}
            <div className="fee-period-banner">
                <div className="banner-left">
                    <Clock size={18} className="banner-clock-icon" />
                    <span>
                        A gerir tabela da <strong>Época {selectedSeason}</strong> (1 de Setembro de {startYearNumber} a 31 de Agosto de {startYearNumber + 1})
                        {isCurrentSeason && <span className="period-current-badge">Época Atual</span>}
                    </span>
                </div>
                <div className="banner-right">
                    <Sparkles size={16} />
                    <span>Os valores aplicam-se a todos os 12 meses desta época desportiva, preservando o histórico das épocas anteriores.</span>
                </div>
            </div>

            {loading ? (
                <div className="fee-manager loading-state" style={{ minHeight: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
                    <Loader2 className="spinner" size={42} style={{ color: 'var(--primary-blue, #003380)' }} />
                    <p style={{ color: '#64748b', fontWeight: 500 }}>A carregar valores da Época {selectedSeason}...</p>
                </div>
            ) : (
                <div className="fee-grid">
                    {/* ── Quota de Sócio Global ─────────────────────────────── */}
                    <div className="fee-card global-quota glass-card animate-in" style={{ '--delay': '0.1s' }}>
                        <div className="fee-card-header no-cursor">
                            <div className="header-icon-box master">
                                <Settings size={22} strokeWidth={2.5} />
                            </div>
                            <div className="header-text">
                                <div className="title-row">
                                    <h3>Quota de Sócio Geral</h3>
                                    <div className="quota-header-badges">
                                        <span className="global-badge">Configuração Global</span>
                                        {isExplicitGlobal ? (
                                            <span className="period-snapshot-badge explicit" title="Definido especificamente para esta época">Fixado nesta época</span>
                                        ) : (
                                            <span className="period-snapshot-badge inherited" title="Herdado da época anterior/base">Herdado</span>
                                        )}
                                    </div>
                                </div>
                                <p>Valores base de sócio para a Época {selectedSeason}.</p>
                            </div>
                        </div>
                        <div className="fee-card-body row-layout global-config-grid">
                            <div className="fee-input-container premium">
                                <div className="input-label-row">
                                    <Users size={16} />
                                    <label>Adulto (≥ 18 anos)</label>
                                </div>
                                <div className="input-group">
                                    <span className="currency-symbol">€</span>
                                    <input type="number" step="0.50" min="0" value={memberFee}
                                        onChange={e => setMemberFee(e.target.value)} />
                                </div>
                            </div>

                            <div className="fee-input-container premium">
                                <div className="input-label-row">
                                    <User size={16} />
                                    <label>Menor (≤ 17 anos)</label>
                                </div>
                                <div className="input-group">
                                    <span className="currency-symbol">€</span>
                                    <input type="number" step="0.50" min="0" value={minorMemberFee}
                                        onChange={e => setMinorMemberFee(e.target.value)} />
                                </div>
                            </div>

                            <div className="fee-input-container premium">
                                <div className="input-label-row">
                                    <Sparkles size={16} />
                                    <label>Jóia de Admissão (Novo Sócio)</label>
                                </div>
                                <div className="input-group">
                                    <span className="currency-symbol">€</span>
                                    <input type="number" step="0.50" min="0" value={admissionFee}
                                        onChange={e => setAdmissionFee(e.target.value)} />
                                </div>
                            </div>

                            <div className="global-save-wrapper">
                                <button className="btn-save-global-premium" onClick={saveGlobalFee} disabled={savingGlobal}>
                                    {savingGlobal ? <Loader2 className="spinner" size={18} /> : <Save size={18} />}
                                    <span>Gravar Tabela Geral (Época {selectedSeason})</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ── Cards por Modalidade ──────────────────────────────── */}
                    {sports.map((sport, index) => {
                        const isExpanded = expandedSports.includes(sport.id);
                        return (
                            <div
                                key={sport.id}
                                className={`fee-card glass-card animate-in ${isExpanded ? 'is-expanded' : ''}`}
                                style={{ '--delay': `${(index + 2) * 0.05}s` }}
                            >
                                <div className="fee-card-header" onClick={() => toggleSportExpand(sport.id)}>
                                    <div className="header-icon-box">
                                        <Euro size={20} />
                                    </div>
                                    <div className="header-text">
                                        <div className="title-row">
                                            <div className="sport-title-wrap">
                                                <h3>{sport.name}</h3>
                                                {sport.isExplicitForSeason ? (
                                                    <span className="period-snapshot-badge explicit" title="Preço específico fixado nesta época">Fixado nesta época</span>
                                                ) : (
                                                    <span className="period-snapshot-badge inherited" title="Herdado da época anterior/base">Herdado</span>
                                                )}
                                            </div>
                                            <div className="header-actions">
                                                <button
                                                    className={`quota-pill ${sport.quotaIncluded ? 'included' : 'separate'}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSportField(sport.id, 'quotaIncluded', !sport.quotaIncluded);
                                                    }}
                                                >
                                                    {sport.quotaIncluded ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                                                    {sport.quotaIncluded ? 'Quota incluída' : 'Quota separada'}
                                                </button>
                                                <ChevronDown className={`expand-icon ${isExpanded ? 'rotated' : ''}`} size={20} />
                                            </div>
                                        </div>
                                        <p>{sport.quotaIncluded ? 'A mensalidade já inclui a quota.' : 'A quota é somada à mensalidade.'}</p>
                                    </div>
                                </div>

                                <div className="fee-card-body-wrapper">
                                    <div className="fee-card-body">
                                        <div className="fee-card-body-inner col-layout">
                                            <section className="fee-section">
                                                <div className="section-header">
                                                    <Info size={14} />
                                                    <span>Mensalidades (Época {selectedSeason})</span>
                                                </div>
                                                <div className="fee-row grid-5">
                                                    <FeeInput label="Normal" field="feeNormalNormal" sport={sport} onChange={handleSportField} />
                                                    <FeeInput label="Esc. 1" field="feeEscalao1Normal" sport={sport} onChange={handleSportField} />
                                                    <FeeInput label="Esc. 2" field="feeEscalao2Normal" sport={sport} onChange={handleSportField} />
                                                    <FeeInput label="Veteranos" field="feeVeteranos" sport={sport} onChange={handleSportField} />
                                                    <FeeInput label="Desc. Irmão" field="feeDiscount" sport={sport} onChange={handleSportField} />
                                                </div>
                                            </section>

                                            <section className="fee-section">
                                                <div className="section-header">
                                                    <Settings size={14} />
                                                    <span>Taxas de Inscrição (Época {selectedSeason})</span>
                                                </div>
                                                <div className="fee-row grid-4">
                                                    <FeeInput label="Normal" field="inscriptionFeeNormal" sport={sport} onChange={handleSportField} />
                                                    <FeeInput label="Minis (< 12 anos)" field="inscriptionFeeMinis" sport={sport} onChange={handleSportField} />
                                                    <FeeInput label="Desc. Minis (< 12)" field="inscriptionFeeMinisDiscount" sport={sport} onChange={handleSportField} />
                                                    <FeeInput label="Veteranos" field="inscriptionFeeVeteranos" sport={sport} onChange={handleSportField} />
                                                </div>
                                            </section>

                                            <section className="fee-section">
                                                <div className="section-header">
                                                    <AlertCircle size={14} />
                                                    <span>Bilhete Anual (Época {selectedSeason})</span>
                                                </div>
                                                <div className="fee-row grid-2">
                                                    <FeeInput label="Preço Sócio" field="annualTicketPriceSocio" sport={sport} onChange={handleSportField} />
                                                    <FeeInput label="Preço Geral" field="annualTicketPriceNonSocio" sport={sport} onChange={handleSportField} />
                                                </div>
                                            </section>

                                            <button
                                                className="btn-save-sport"
                                                onClick={(e) => saveSportFee(e, sport)}
                                                disabled={savingSport === sport.id}
                                            >
                                                {savingSport === sport.id ? <Loader2 className="spinner" size={18} /> : <Save size={18} />}
                                                <span>Guardar {sport.name} (Época {selectedSeason})</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default FeeManager;